import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject, Subscription} from 'rxjs';

import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BimAndPlanPin, PinService} from '../pin-service/pin-service';
import {MarkerIconTypeEnum, TileViewerReadyEvent} from '@mydocma/tile-viewer';
import {BimPoint} from '../../../bp-common/models/plan';
import {PlanLocationService} from '../location-services/plan-location.service';
import {Pin as PlanPin} from '@mydocma/tile-viewer/lib/pin/pin';
import {Guid} from 'guid-typescript';
import {CanvasClickHitEvent, Pin, PopupTypeEnum, ViewerReadyParams} from '@mydocma/bim-viewer';
import {
  CoordinatesTransformationService
} from '../coordinates-transformation-service/coordinates-transformation.service';
import {Point} from 'leaflet';
import {CreationPopupComponent, PopupCloseResult} from '../../creation-popup/creation-popup.component';
import {StartCreationParams} from './start-creation-params';
import {filter, first} from 'rxjs/operators';
import {PinCreationState} from './pin-creation-state-enum';
import {StopCreationParams} from './stop-creation-params';

import {PopTestComponent} from '../helpers/pop-test/pop-test.component';
import {PlanViewerService} from '../plan-viewer/plan-viewer.service';
import {BimNotificationService} from '../helpers/bim-notification.service';
import { Logger } from '@mydocma/common/src/lib/logger';
import {ObjectSelectionService} from '../location-services/object-selection.service';
import {BpPin, BpPinLocationTargetEnum} from '../../../bp-common/models/bp-pin';
import {MmBimViewerService} from '../bim-viewer/mm-bim-viewer.service';
import {MmBimService} from '../../mm-bim.service';

export type BimPlanIds = { bimId: string, planId: string };

@UntilDestroy()
@Injectable()
export class CreationService {
  public bimViewerData: ViewerReadyParams;
  public objectSelectionBypassed = false;
  public currentBimEditPin: Pin;
  private planReadyData: TileViewerReadyEvent;
  private pinCreationStateSubject = new BehaviorSubject<PinCreationState>(PinCreationState.Idle);
  public pinCreationState$ = this.pinCreationStateSubject.asObservable();
  private bimModelObjectClickSubscription: Subscription;
  private planEditPin: PlanPin;

  private popUpCloseSubject = new Subject<PopupCloseResult>();

  private planLocationMovementSubscription: Subscription;

  private toolbar?: HTMLElement;

  private hideToolbar(): void {
    if (!this.toolbar) {
      this.toolbar = this.bimViewerData.viewerService.forgeViewer.container
        .querySelector('#guiviewer3d-toolbar') as HTMLElement | null;
    }
    if (this.toolbar) {
      this.toolbar.style.display = 'none';
    }
  }

  private showToolbar(): void {
    if (this.toolbar) {
      this.toolbar.style.display = '';
    }
  }

  constructor(
    private pinService: PinService,
    private bimService: MmBimService,
    private objectSelectionService: ObjectSelectionService,
    private planLocationService: PlanLocationService,
    private bimNotificationService: BimNotificationService,
    private mmBimViewerService: MmBimViewerService,
    private transformationService: CoordinatesTransformationService,
    private readonly logger: Logger) {
    this.pinCreationState$.pipe(untilDestroyed(this)).subscribe(value => {
      value === PinCreationState.PrepareSave ? this.hideToolbar() : this.showToolbar();
    });
  }

  get creationState() {
    return this.pinCreationStateSubject.value;
  }

  public notifyCreationPopupClose(results: PopupCloseResult) {
    this.popUpCloseSubject.next(results);
  }

  // starting point of service, depends on given options starts creation mode in specific mode
  // skipObjectSelected if true will skip object selection step, and will move right last step of selecting area for snapshot
  // for end results
  public async startPinCreation(options: StartCreationParams, pin: Pin = null): Promise<void> {

    try {
      if (options?.pendPlanSelection || !this.bimService.selectedPlan) {
        this.updatePinCreationState(PinCreationState.WaitForPlanSelection);
        const viewerForPlanAndBimReady = await firstValueFrom(this.bimNotificationService.bimAndPlanViewerReady$
          .pipe(filter(Boolean), first()));
        // if cancel was clicked;
        if (this.creationState === PinCreationState.Idle) {
          return;
        }
      }
      this.pinService.selectPin(null);
      this.objectSelectionService.disableObjectSelectionMode();
      if (this.bimViewerData.viewerService.propertyVisibility) {
        this.bimViewerData.viewerService.hidePropertiesForObject();
      }
      if (options?.hideExistingPins) {
        this.pinService.hideExistingPins();
      }
      if (!options?.skipObjectSelected) {
        this.setupCanvasClickSubscription();
      }

      if (pin) {
        this.addPinToBimModelAndMap(null, pin, options?.skipObjectSelected);
        return;
      }
      this.updatePinCreationState(PinCreationState.EnterCreationMode);
    } catch (e) {
      this.endPinCreation({endWithPin: false});
      this.logger.error(e, '', 'CreationService');
    }

  }

  public attach(bimViewerData: ViewerReadyParams, tileReady: TileViewerReadyEvent): void {
    this.bimViewerData = bimViewerData;
    this.planReadyData = tileReady;
  }

  // end point, end creation mode with given params, set viewer events to default state
  public async endPinCreation(params: StopCreationParams, cancel?: boolean): Promise<BimAndPlanPin> {
    try {
      if (this.bimViewerData && this.planReadyData) {


        this.toggleEventListenersAndCamera(false);
        if (this.currentBimEditPin) {
          this.bimViewerData.popupService.closePopup(this.currentBimEditPin.id);
        }
        const stopPinEditParams = await this.planReadyData.pinEditService.stopEdit(true);
        this.pinService.showExistingPins();
        this.planLocationService.showLocationMarker();
        this.objectSelectionService.enableObjectSelectionMode();

        if (cancel) {
          this.handleCancellation();
          this.objectSelectionBypassed = false;
          return;
        }
        this.bimViewerData.selectionAreaService.removeSelectionArea();
        this.objectSelectionBypassed = false;
        let pin;
        if (params?.endWithPin) {
          this.currentBimEditPin.popupComponent = PopTestComponent;
          delete this.currentBimEditPin.markerOptions.disablePopupMouseEvents;
          pin = {
            bimPin: this.currentBimEditPin,
            planPin: this.planEditPin,
            id: this.currentBimEditPin.id
          };
          pin.planPin.id = pin.id;
          pin.bimPin.id = pin.id;
        }
        this.updatePinCreationState(PinCreationState.Idle);
        return pin;
      } else {
        this.updatePinCreationState(PinCreationState.Idle);
      }
    } catch (e) {

      this.endPinCreation({endWithPin: false});
      this.logger.error(e, '', 'CreationService');

    }
  }

  public async cancelPrepareSave(): Promise<void> {
    try {
      this.toggleEventListenersAndCamera(false);
      const stopPinEditParams = await this.planReadyData.pinEditService.stopEdit(true);
      this.bimViewerData.selectionAreaService.removeSelectionArea();
      this.planLocationService.enableLocationEvents();
      this.planLocationService.showLocationMarker();
      this.startPinCreation({hideExistingPins: true}, this.currentBimEditPin);
      this.bimViewerData.viewerService.forgeViewer.select([this.currentBimEditPin.bimElementId]);
    } catch (e) {
      this.endPinCreation({endWithPin: false});
      this.logger.error(e, '', 'CreationService');
    }

  }

  // by default, it either resets to creationMode(no pins, selected in model, or cancel creation mode to idl)
  // take param endPinCreation which will end creation mode, does not matter what pin creation state is
  public async cancelCreation(endPinCreation = false) {
    if (endPinCreation) {
      await this.endPinCreation({endWithPin: false}, true);
      return;
    }
    if (this.creationState === PinCreationState.ObjectSelected) {
      this.cleanupAfterObjectSelection();

    } else {
      await this.endPinCreation({endWithPin: false}, true);
    }
  }

  public async startSnapshotMode() {
    const {popupService, selectionAreaService, cameraService, viewerService} = this.bimViewerData;
    popupService.closePopup(this.currentBimEditPin.id);
    this.planLocationService.hideLocationMarker();
    if (viewerService.propertyVisibility) {
      viewerService.hidePropertiesForObject();
    }
    // disable camera movement
    const bimPoint = this.currentBimEditPin.point;
    // disable camera movement for bim viewer
    this.toggleEventListenersAndCamera(true);
    selectionAreaService.createResizableArea(bimPoint);
    this.planReadyData.pinEditService.startEdit({
      hideExistingPins: true,
      showSelectionArea: true,
      disablePinDragging: true
    }, this.planEditPin);
    // todo when create pin with bypass object selection, its currentBimEditPin not adding to viewer, unles with setTimout
    setTimeout(() => {
      this.bimViewerData.pinService.setPins([this.currentBimEditPin]);
    }, 0);


    // disable click event for tile viewer
    // todo add disable panorama zoom event


    this.pinCreationStateSubject.next(PinCreationState.PrepareSave);

  }

  public async getPin(): Promise<BpPin> {


    const bimModelVersion = await firstValueFrom(this.mmBimViewerService.currentBimModelVersion$);
    const bimSectionPlan = this.bimService.getSectionPlan(this.bimService.selectedPlan?.id.toString());

    const pin: BpPin = {
      Locations: [
        {
          Type: BpPinLocationTargetEnum.plan,
          ModelId: bimSectionPlan.planId,
          VersionId: bimSectionPlan.versionId,
          X: this.planEditPin.anchor.x,
          Y: this.planEditPin.anchor.y,
          Marker: (this.planEditPin?.markerAnchor ? {
            Location: {
              X: this.planEditPin.markerAnchor.x,
              Y: this.planEditPin.markerAnchor.y
            }
          } : undefined)
        },
        {
          Type: BpPinLocationTargetEnum.bim,
          ModelId: bimModelVersion.bimModelId,
          VersionId: bimModelVersion.id,
          X: this.currentBimEditPin.point.x,
          Y: this.currentBimEditPin.point.y,
          Z: this.currentBimEditPin.point.z,
          AdditionalData: {
            bimElementId: this.currentBimEditPin.bimElementId,
            camera: this.currentBimEditPin.camera
          }
        },
      ]

    };
    return pin;

  }

  public async getSnapshots(): Promise<{ bimSnapshot: Blob; planSnapshot: Blob } | null> {
    if (this.creationState !== PinCreationState.PrepareSave) {
      return null;
    }
    if (!toolbar) {
      throw new Error('Viewer toolbar not found');
    }


    try {
      await new Promise(resolve => setTimeout(resolve, 0));

      const bounds = this.planReadyData.selectionAreaService.getSelectionAreaBounds();
      const start = Date.now();

      const [bimSnapshot, planSnapshot] = await Promise.all([
        this.bimViewerData.selectionAreaService.getSelectionAreaSnapShot(),
        this.planReadyData.snapshotAreaService.takeSnapshot({
          moveToSelection: true,
          shapshotBounds: bounds
        })
      ]);

      console.log(`Plan/Bim snapshots took ${(Date.now() - start) / 1000} s`);
      return {bimSnapshot, planSnapshot};
    } finally {
    }
  }


  private cleanupAfterObjectSelection() {
    this.unsubscribeEvents();
    this.resetPinsAndPopup();
    this.startPinCreationWithHiddenPins();
  }

  private startPinCreationWithHiddenPins() {
    this.startPinCreation({hideExistingPins: true});
  }

  private setupPinMovementSubscription() {
    this.planLocationMovementSubscription?.unsubscribe();
    this.planLocationMovementSubscription = this.planLocationService.onMarkerLocationChange.subscribe(() => {
      this.cancelCreation();
    });
  }

  private setupCanvasClickSubscription(): void {
    this.bimModelObjectClickSubscription?.unsubscribe();
    this.bimModelObjectClickSubscription = this.bimViewerData.eventListenerService.onCanvasClickHit
      .subscribe((value) => {
        this.addPinToBimModelAndMap(value);
      });
  }

  private toggleEventListenersAndCamera(enable: boolean): void {
    if (enable) {

      this.bimViewerData.cameraService.toggleCameraMovement(true);
      // disable object selection listen to selection in viewer and then remove selections
      this.bimViewerData.viewerService.disableObjectSelection();
      this.bimModelObjectClickSubscription?.unsubscribe(); //
      this.planLocationService.disableLocationEvents();
      this.bimViewerData.cameraService.disablePanoramaZoom();
      this.bimViewerData.viewerService.forgeViewer.clearSelection();
    } else {
      this.bimViewerData.viewerService.enableObjectSelection();
      this.bimModelObjectClickSubscription?.unsubscribe();
      this.planLocationMovementSubscription?.unsubscribe();
      this.bimViewerData.cameraService.enablePanoramaZoom();
      this.bimViewerData.cameraService.toggleCameraMovement(false);
      this.planLocationService.enableLocationEvents();
    }
  }

  private handleCancellation(): void {
    if (this.bimViewerData.selectionAreaService.selectionArea) {
      this.bimViewerData.selectionAreaService.removeSelectionArea();
    }
    this.updatePinCreationState(PinCreationState.Idle);
  }

  private unsubscribeEvents(): void {
    this.bimModelObjectClickSubscription?.unsubscribe();
    this.planLocationMovementSubscription?.unsubscribe();
  }

  // method  creates two pins on bim model and plan,from given pin or coords from click event, and add s them into viewer
  // creates subscription which removes those pins if location on plan was changed

  private resetPinsAndPopup(): void {
    const {pinService, popupService, viewerService} = this.bimViewerData;
    viewerService.forgeViewer.clearSelection();
    popupService.closePopup(this.currentBimEditPin.id);
    this.bimViewerData.pinService.setPins([]);
    this.planReadyData.pinService.setPins([]);
  }

  private updatePinCreationState(state: PinCreationState): void {
    this.pinCreationStateSubject.next(state);
  }

  // this method end with res from popup in success, then it go to next step, by saving pins and taking snaphsit of each viewer
  private async addPinToBimModelAndMap(e?: CanvasClickHitEvent, pin?: Pin, bypassObjectSelection = false): Promise<void> {
    if (bypassObjectSelection) {
      this.processPinWithoutObjectSelection(pin);
    } else {
      this.processPinWithSelection(e, pin);
    }

  }

  // creates popup near pin, and waits for results form its

  private async processPinWithSelection(e: CanvasClickHitEvent, pin: Pin): Promise<void> {
    this.pinCreationStateSubject.next(PinCreationState.ObjectSelected);
    this.setupPinMovementSubscription();
    this.closeCurrentBimEditPinPopup();

    if (!e) {
      this.updateGivenPinWithData(pin);
    } else {
      const {worldCoords, selectedElemId} = e.detail;
      this.createPinFormEventDetails({worldCoords, selectedElemId});
    }
    this.createAndAddPins();
    await this.finalizePinCreation();
  }

  private closeCurrentBimEditPinPopup(): void {
    if (this.currentBimEditPin) {
      this.bimViewerData.popupService.closePopup(this.currentBimEditPin.id);
    }
  }

  // if success moves to next step of selecting area for snapshot
  private async finalizePinCreation(): Promise<void> {
    const result = await this.showCreationPopup();
    if (result === PopupCloseResult.success) {
      await this.startSnapshotMode();
    } else {
      await this.cancelCreation();
    }
  }

  private createAndAddPins(): void {
    this.planEditPin = this.createPlanPinFromBimPin(this.currentBimEditPin);
    this.bimViewerData.pinService.setPins([this.currentBimEditPin]);
    this.planReadyData.pinService.setPins([this.planEditPin]);
    this.bimViewerData.cameraService.notifyCameraPositionChange();
  }

  private processPinWithoutObjectSelection(pin) {
    this.objectSelectionBypassed = true;
    this.updateGivenPinWithData(pin);
    this.planEditPin = this.createPlanPinFromBimPin(this.currentBimEditPin);
    this.startSnapshotMode();
  }

  private createPlanPinFromBimPin(bimPin: Pin): PlanPin {
    const projectPoint = this.transformationService.transformToPlanCoordinates(bimPin.point as BimPoint) as Point;
    const planCoords = this.transformationService.projectToRelative(projectPoint);

    return {
      id: this.currentBimEditPin.id,
      anchor: planCoords,
      markerOptions: {iconType: MarkerIconTypeEnum.defaultWithObject}
    } as PlanPin;
  }

  private async showCreationPopup(): Promise<PopupCloseResult> {
    this.bimViewerData.popupService.showPopup(this.currentBimEditPin.id, {popupType: PopupTypeEnum.default});
    return this.popUpCloseSubject.pipe(first()).toPromise();
  }

  private createPinFormEventDetails({worldCoords, selectedElemId}): void {
    const id = Guid.create().toString();
    this.currentBimEditPin = {
      id,
      point: worldCoords,
      markerOptions: {
        iconType: MarkerIconTypeEnum.default,
        disablePopupMouseEvents: true,
      },
      draggable: false,
      popupComponent: CreationPopupComponent,
      bimElementId: selectedElemId,
      camera: this.bimViewerData.cameraService.getCameraPosition()
    } as unknown as Pin;
  }

  private updateGivenPinWithData(pin: Pin): void {
    this.currentBimEditPin = pin;
    this.currentBimEditPin.markerOptions = {
      iconType: MarkerIconTypeEnum.default,
      disablePopupMouseEvents: true,
    } as any;
    this.currentBimEditPin.popupComponent = CreationPopupComponent;
    this.currentBimEditPin.draggable = false;
    this.currentBimEditPin.camera = this.currentBimEditPin.camera || this.bimViewerData.cameraService.getCameraPosition();
  }

}
